import React from 'react';
import { generatePath, Redirect, Switch } from 'react-router-dom';

import { useAppSelector } from 'flow/hooks/useRedux';
import Loading from 'flow/components/ui-components/Loading';
import { routes } from 'flow/types/routes';
import { selectIsSinglePaneMode } from 'flow/selectors/app-selectors';
import {
    selectDefaultPBXKey,
    selectPbxIsLoaded,
    SUPERVISOR_VIEW_KEY,
} from 'flow/selectors/queue-selectors';
import {
    isChannelKey,
    isConnectionKey,
    isQueueKey,
    isReferKey,
    isVoicemailKey,
} from 'flow/utils';

/**
 * The pbx page for a default key, or undefined for a key with no page.
 *
 * Every target stays inside the pbx tab - see selectDefaultPBXKey.
 */
const getPbxPageForEntityKey = (entityKey?: string) => {
    if (entityKey === SUPERVISOR_VIEW_KEY) {
        return routes['pbx.overview'];
    }
    if (isQueueKey(entityKey)) {
        return generatePath(routes['pbx.queues.entityKey'], { entityKey });
    }
    if (isReferKey(entityKey)) {
        return generatePath(routes['pbx.refers.entityKey'], { entityKey });
    }
    if (isChannelKey(entityKey)) {
        return generatePath(routes['pbx.channels.entityKey'], { entityKey });
    }
    if (isVoicemailKey(entityKey)) {
        return generatePath(routes['pbx.voicemails.entityKey'], { entityKey });
    }
    if (isConnectionKey(entityKey)) {
        return generatePath(routes['pbx.connections.connectionKey'], {
            connectionKey: entityKey,
        });
    }

    return undefined;
};

const LandingPagePbx = () => {
    const isSinglePaneMode = useAppSelector(selectIsSinglePaneMode);

    const pbxIsLoaded = useAppSelector(selectPbxIsLoaded);
    const entityKey = useAppSelector(selectDefaultPBXKey);

    if (!pbxIsLoaded) {
        return <Loading />;
    }

    const to = isSinglePaneMode
        ? routes['pbx']
        : getPbxPageForEntityKey(entityKey);

    if (!to) {
        // An account can have nothing to land on, so the spinner must stop
        // once the pbx has answered. The empty pane matches LandingPageCalls.
        return (
            <div className="flex flex-auto border-s border-solid bg-white" />
        );
    }

    return (
        <Switch>
            <Redirect path="/" to={to} />
        </Switch>
    );
};

export default LandingPagePbx;
